import { styled } from '@linaria/react'
import React, {
  type ComponentProps,
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import DatePicker, { registerLocale, setDefaultLocale } from 'react-datepicker'
import { createEnUSLocale, createRuLocale } from '../shared/utils/locales'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { Switch } from '../ui/Switch'
import { InfoCard } from '../ui/InfoCard'
import { InfoHint } from '../ui/InfoHint'
import { ButtonRegular } from '../ui/Button'
import { Input } from '../ui/Input'
import { ErrorText } from '../ui/ErrorText'
import { colors } from '../styles/colors'
import { Typography } from '../ui/Typography'
import { OptionInput } from './OptionInput'
import { useAppContext } from '../contexts/App.context'
import { Select } from '../ui/Select'
import PlusIcon from '../svgr/Plus'
import QuestionIcon from '../svgr/Faq'
import {
  getTimezoneSelectOptions,
  getUserTimezone,
} from '../shared/utils/timezones'
import 'react-datepicker/dist/react-datepicker.css'
import 'dayjs/locale/ru'
import 'dayjs/locale/en'
import { UploadImageButton } from './UploadImageButton'
import { CountdownTimer } from './CountdownTimer'
import { CreateBoxConfirmDialog } from '../dialogs/CreateBoxConfirmDialog'
import { formatPercentInBasisPoints } from '../functions/formatPercentInBasisPoints'
import { useCreateBox } from '../hooks/useCreateBox'
import { BottomSheet, useBottomSheet } from './BottomSheet/BottomSheet'
import { formatTon } from '../utils/formatTon'
import { HowItWorksContent } from './HowItWorksContent'
import { useFormLocalStorage } from '../hooks/useFormLocalStorage'
import { loadFormDraft, removeFormDraft } from '../shared/utils/formDraft'
import { useActiveBoxCollections } from '../hooks/useActiveBoxCollections'
import { CREATOR_EARNINGS_PERCENT } from '../constants'

export interface FormData {
  question: string
  options: { value: string }[]
  deadline: Date | null
  commission: number
  timezone: string
  imageUrl: string | null
  publishToFeed: boolean
  multipleChoice: boolean
  quizMode: boolean
}

dayjs.extend(utc)
dayjs.extend(timezone)

// Form default values
const FORM_DEFAULTS: FormData = {
  question: '',
  options: [{ value: '' }, { value: '' }],
  deadline: null,
  commission: CREATOR_EARNINGS_PERCENT,
  timezone: getUserTimezone(),
  publishToFeed: true,
  imageUrl: null,
  multipleChoice: false,
  quizMode: false,
}

// Form persistence constants
const STORAGE_KEY = 'votebox_new_box_form'
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

export const NewBoxForm: FC<{
  className?: string
}> = ({ className }) => {
  const { language } = useAppContext()
  const { formatMessage } = useIntl()
  const { sendCreateBoxMessage } = useCreateBox()
  const simpleSheet = useBottomSheet()
  const howItWorksSheet = useBottomSheet()

  // Fetch settings from API
  const { data: boxCollections } = useActiveBoxCollections()
  const activeCollection = boxCollections?.[0]
  const settings = activeCollection?.settings

  // Derive settings values with fallback defaults
  const boxCreationFee = settings?.box_creation_fee
    ? BigInt(settings.box_creation_fee)
    : undefined
  const minDurationDays = settings?.min_duration
    ? Math.ceil(Number(settings.min_duration) / (24 * 60 * 60))
    : 3
  const maxDurationDays = settings?.max_duration
    ? Math.floor(Number(settings.max_duration) / (24 * 60 * 60))
    : 120
  const maxChoices = settings?.max_choices ? Number(settings.max_choices) : 10

  // Load draft from localStorage before initializing form
  const initialValues = useMemo(() => {
    const rawInitialValues = loadFormDraft<FormData>(STORAGE_KEY, DRAFT_TTL_MS)
    if (!rawInitialValues) {
      return null
    }

    return {
      ...rawInitialValues,
      deadline:
        rawInitialValues.deadline instanceof Date
          ? rawInitialValues.deadline
          : rawInitialValues.deadline
            ? new Date(rawInitialValues.deadline)
            : null,
    }
  }, [])

  // Clear draft function
  const clearDraft = useCallback(() => {
    removeFormDraft(STORAGE_KEY)
  }, [])

  // Refs for scrolling to errors
  const questionRef = useRef<HTMLDivElement>(null)
  const optionsRef = useRef<HTMLDivElement>(null)
  const deadlineRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLDivElement>(null)

  // Calculate min and max selectable dates for DatePicker
  const minSelectableDate = useMemo(() => {
    const date = new Date()
    date.setDate(date.getDate() + minDurationDays)
    return date
  }, [minDurationDays])

  const maxSelectableDate = useMemo(() => {
    const date = new Date()
    date.setDate(date.getDate() + maxDurationDays)
    return date
  }, [maxDurationDays])

  // Filter times to only allow hours within the valid range on boundary dates
  const filterTime = useCallback(
    (time: Date) => {
      const now = new Date()
      const minTime = new Date(
        now.getTime() + minDurationDays * 24 * 60 * 60 * 1000,
      )
      const maxTime = new Date(
        now.getTime() + maxDurationDays * 24 * 60 * 60 * 1000,
      )

      return (
        time.getTime() >= minTime.getTime() &&
        time.getTime() <= maxTime.getTime()
      )
    },
    [minDurationDays, maxDurationDays],
  )

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    trigger,
    reset,
  } = useForm<FormData>({
    defaultValues: {
      ...FORM_DEFAULTS,
      ...initialValues, // Merge with loaded draft (draft takes precedence)
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  // Start auto-save after form is initialized
  useFormLocalStorage<FormData>({
    key: STORAGE_KEY,
    watch,
    debounceMs: 1000,
    ttlMs: DRAFT_TTL_MS,
  })

  const handleNewBoxFormSubmit = useCallback(
    async (data: FormData) => {
      if (!data.deadline || !boxCreationFee) {
        const error = new Error('Missing deadline or box creation fee')
        console.error(error.message)
        throw error
      }

      try {
        // Interpret displayed deadline as being in selected timezone and convert to UTC
        const displayedTime = dayjs(data.deadline).format('YYYY-MM-DD HH:mm')
        const utcTimestamp = dayjs
          .tz(displayedTime, data.timezone || 'UTC')
          .unix()

        // Разкомментировать для тестирования
        // return Promise.resolve()

        await sendCreateBoxMessage(
          {
            name: data.question.trim(),
            imageUrl: data.imageUrl,
            choices: data.options.map(option => option.value.trim()),
            endTimeInSeconds: BigInt(utcTimestamp),
            creatorBasisPoints: BigInt(
              formatPercentInBasisPoints(Number(data.commission)),
            ),
            publishToFeed: data.publishToFeed,
          },
          boxCreationFee,
        )

        // Clear draft and reset form only after successful submission
        clearDraft()
        reset(FORM_DEFAULTS)
      } catch (error) {
        // Don't clear draft or reset form on error - preserve user data
        console.error('Failed to create box:', error)
        throw error // Re-throw to let error handling in parent components work
      }
    },
    [sendCreateBoxMessage, boxCreationFee, clearDraft, reset],
  )

  const handleCreateButtonClick = async () => {
    const isFormValid = await trigger()

    if (isFormValid) {
      simpleSheet.open()
    } else {
      // Scroll to the first error
      if (errors.question && questionRef.current) {
        questionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      } else if (errors.options && optionsRef.current) {
        optionsRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      } else if (errors.deadline && deadlineRef.current) {
        deadlineRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      } else if (errors.imageUrl && imageRef.current) {
        imageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }

  const watchedTimezone = watch('timezone')
  const watchedDeadline = watch('deadline')
  const watchedCommission = watch('commission')

  // Calculate UTC time based on the displayed time and selected timezone
  const deadlineUtc = useMemo(() => {
    if (!watchedDeadline) {
      return null
    }
    // Interpret the displayed time (local Date) as being in the selected timezone
    const displayedTime = dayjs(watchedDeadline).format('YYYY-MM-DD HH:mm')
    return dayjs.tz(displayedTime, watchedTimezone || 'UTC')
  }, [watchedDeadline, watchedTimezone])

  // Set up dayjs and DatePicker locales
  useEffect(() => {
    dayjs.locale(language)
    registerLocale('ru', createRuLocale(formatMessage))
    registerLocale('en', createEnUSLocale(formatMessage))
    setDefaultLocale(language)
  }, [language, formatMessage])

  const watchedOptions = watch('options')

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'options',
    rules: {
      minLength: {
        value: 2,
        message: formatMessage({
          id: 'newBox.form.optionsMinCount',
          defaultMessage: 'Минимум 2 варианта ответа',
        }),
      },
    },
  })

  const addOption = () => {
    if (fields.length < maxChoices) {
      append({ value: '' })
    }
  }

  const removeOption = (index: number) => {
    if (fields.length > 2) {
      remove(index)
    }
  }

  const validateQuestionName = (value: string) => {
    if (!value) {
      return true
    }

    // Check if only contains allowed characters (letters, digits, spaces, punctuation)
    if (!/^[a-zA-Zа-яА-ЯёЁ0-9\s.,?&:'-]+$/.test(value)) {
      return formatMessage({
        id: 'newBox.form.questionAlphanumeric',
        defaultMessage: "Только буквы, цифры, пробелы и знаки . , ? & : ' -",
      })
    }

    // Check if starts with . or ,
    if (/^[.,]/.test(value)) {
      return formatMessage({
        id: 'newBox.form.questionInvalidStart',
        defaultMessage: 'Не может начинаться с точки или запятой',
      })
    }

    // Check if ends with ,
    if (value.endsWith(',')) {
      return formatMessage({
        id: 'newBox.form.questionInvalidEnd',
        defaultMessage: 'Не может заканчиваться запятой',
      })
    }

    // Check if ? appears anywhere except at the end
    const questionMarkIndex = value.indexOf('?')
    if (questionMarkIndex !== -1 && questionMarkIndex !== value.length - 1) {
      return formatMessage({
        id: 'newBox.form.questionMarkOnlyAtEnd',
        defaultMessage: 'Знак вопроса может быть только в конце',
      })
    }

    // Check if there are multiple ?
    if ((value.match(/\?/g) || []).length > 1) {
      return formatMessage({
        id: 'newBox.form.questionMarkOnlyAtEnd',
        defaultMessage: 'Знак вопроса может быть только в конце',
      })
    }

    return true
  }

  const validateUniqueOption = (value: string, index: number) => {
    if (!value.trim()) {
      return true
    }

    const currentValues = watchedOptions
      .map((option, i) => ({
        value: option.value.trim().toLowerCase(),
        index: i,
      }))
      .filter(item => item.index !== index && item.value !== '')

    const isDuplicate = currentValues.some(
      item => item.value === value.trim().toLowerCase(),
    )

    return isDuplicate
      ? formatMessage({
          id: 'newBox.form.optionDuplicate',
          defaultMessage: 'Такой вариант уже есть',
        })
      : true
  }

  const validateDeadlineRange = (value: Date | null) => {
    if (!value) {
      return true
    }

    // Calculate UTC time using the selected timezone (same logic as deadlineUtc)
    const displayedTime = dayjs(value).format('YYYY-MM-DD HH:mm')
    const utcTime = dayjs.tz(displayedTime, watchedTimezone || 'UTC')

    const now = dayjs()
    const diffMs = utcTime.valueOf() - now.valueOf()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays < minDurationDays) {
      return formatMessage(
        {
          id: 'newBox.form.deadlineTooSoon',
          defaultMessage: 'Минимум {days} дней от текущего времени',
        },
        { days: minDurationDays },
      )
    }

    if (diffDays > maxDurationDays) {
      return formatMessage(
        {
          id: 'newBox.form.deadlineTooFar',
          defaultMessage: 'Максимум {days} дней от текущего времени',
        },
        { days: maxDurationDays },
      )
    }

    return true
  }

  return (
    <Container className={className}>
      <InfoCard icon={<StyledQuestionIcon />} onClick={howItWorksSheet.open}>
        <FormattedMessage
          id="newBox.howItWorks"
          defaultMessage="Как это работает?"
        />
      </InfoCard>

      <FormSection ref={questionRef}>
        <Controller
          name="question"
          control={control}
          rules={{
            required: formatMessage({
              id: 'newBox.form.questionRequired',
              defaultMessage: 'Вопрос голосования обязателен',
            }),

            minLength: {
              value: 5,

              message: formatMessage({
                id: 'newBox.form.questionMinLength',
                defaultMessage: 'Минимум 5 символов',
              }),
            },

            maxLength: {
              value: 200,

              message: formatMessage({
                id: 'newBox.form.questionMaxLength',
                defaultMessage: 'Максимум 200 символов',
              }),
            },

            validate: validateQuestionName,
          }}
          render={({ field }) => (
            <>
              <Input
                label={
                  <Typography fontSize={12} fontWeight={400}>
                    {formatMessage({
                      id: 'newBox.form.questionLabel',
                      defaultMessage: 'Вопрос бокса',
                    })}
                  </Typography>
                }
                placeholder={formatMessage({
                  id: 'newBox.form.questionPlaceholder',
                  defaultMessage: 'Введите вопрос',
                })}
                {...field}
              />

              {errors.question && (
                <ErrorText>{errors.question.message}</ErrorText>
              )}
            </>
          )}
        />
      </FormSection>

      <FormSection ref={optionsRef}>
        <SectionLabel>
          {formatMessage({
            id: 'newBox.form.optionsLabel',
            defaultMessage: 'Варианты ответа',
          })}
        </SectionLabel>

        {fields.map((field, index) => (
          <Controller
            key={field.id}
            name={`options.${index}.value`}
            control={control}
            rules={{
              required: formatMessage({
                id: 'newBox.form.optionRequired',
                defaultMessage: 'Вариант ответа не может быть пустым',
              }),
              minLength: {
                value: 1,
                message: formatMessage({
                  id: 'newBox.form.optionMinLength',
                  defaultMessage: 'Минимум 1 символ',
                }),
              },
              maxLength: {
                value: 100,
                message: formatMessage({
                  id: 'newBox.form.optionMaxLength',
                  defaultMessage: 'Максимум 100 символов',
                }),
              },
              validate: value => validateUniqueOption(value, index),
            }}
            render={({ field: inputField, fieldState }) => (
              <OptionInput
                {...inputField}
                onChange={inputField.onChange}
                placeholder={formatMessage({
                  id: 'newBox.form.optionPlaceholder',
                  defaultMessage: 'Введите вариант ответа',
                })}
                showRemove={fields.length > 2}
                onRemove={() => removeOption(index)}
                hasError={Boolean(fieldState.error)}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        ))}

        {errors.options && <ErrorText>{errors.options.message}</ErrorText>}

        {fields.length < maxChoices && (
          <AddButton type="button" onClick={addOption}>
            <AddOptionButtonContent>
              <StyledPlusIcon />
              {formatMessage({
                id: 'newBox.addOption',
                defaultMessage: 'Добавить',
              })}
            </AddOptionButtonContent>
          </AddButton>
        )}
      </FormSection>

      <FormSection ref={deadlineRef}>
        <SectionLabel>
          {formatMessage({
            id: 'newBox.deadline',
            defaultMessage: 'Время окончания бокса',
          })}
        </SectionLabel>

        <InfoHint>
          {formatMessage({
            id: 'newBox.timezoneHint',
            defaultMessage:
              'Учитывайте свой часовой пояс или выберите подходящий',
          })}
        </InfoHint>

        <Controller
          name="timezone"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              options={getTimezoneSelectOptions(formatMessage)}
              placeholder={formatMessage({
                id: 'newBox.timezonePlaceholder',
                defaultMessage: 'Выберите часовой пояс',
              })}
            />
          )}
        />

        <Controller
          name="deadline"
          control={control}
          rules={{
            required: formatMessage({
              id: 'newBox.deadlineRequired',
              defaultMessage: 'Дедлайн обязателен',
            }),
            validate: validateDeadlineRange,
          }}
          render={({ field }) => (
            <>
              <DatePickerWrapper>
                <DatePicker
                  selected={field.value}
                  onChange={(date: Date | null) => {
                    // Store the date as-is (displayed time)
                    // UTC conversion happens via deadlineUtc when needed
                    field.onChange(date)
                  }}
                  onChangeRaw={e => e?.preventDefault()}
                  onFocus={e => e.target.blur()}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={60}
                  dateFormat="dd.MM.yyyy HH:mm"
                  minDate={minSelectableDate}
                  maxDate={maxSelectableDate}
                  filterTime={filterTime}
                  locale={language === 'ru' ? 'ru' : 'en'}
                  placeholderText={formatMessage({
                    id: 'newBox.deadlinePlaceholder',
                    defaultMessage: 'Выберите дату и время',
                  })}
                  wrapperClassName="date-picker-wrapper"
                  className="date-picker-input"
                />
              </DatePickerWrapper>

              {errors.deadline && (
                <ErrorText>{errors.deadline.message}</ErrorText>
              )}

              <TimezoneInfo>
                {deadlineUtc && (
                  <Typography fontSize={10} fontWeight={400}>
                    {formatMessage(
                      {
                        id: 'newBox.timezoneInfoUtc',
                        defaultMessage: 'Время окончания: {time} UTC',
                      },
                      {
                        time: deadlineUtc.utc().format('DD.MM.YYYY HH:mm'),
                      },
                    )}
                  </Typography>
                )}
                <CountdownTimer targetDate={deadlineUtc} />
              </TimezoneInfo>
            </>
          )}
        />
      </FormSection>

      <FormSection>
        <SectionLabel>
          {formatMessage({
            id: 'newBox.commissionLabel',
            defaultMessage: 'Ваша награда',
          })}
        </SectionLabel>

        <InfoCard>
          <Typography fontSize={14} fontWeight={400}>
            {formatMessage(
              {
                id: 'newBox.commissionFixed',
                defaultMessage: '{percent}% от общих застейканных TON',
              },
              { percent: watchedCommission },
            )}
          </Typography>
        </InfoCard>
      </FormSection>

      <FormSection ref={imageRef}>
        <SectionLabel>
          {formatMessage({
            id: 'newBox.form.imageLabel',
            defaultMessage: 'Изображение',
          })}
        </SectionLabel>

        <Controller
          name="imageUrl"
          control={control}
          render={({ field }) => (
            <>
              <UploadImageButton
                value={field.value}
                onImageSelect={url => field.onChange(url)}
                onImageRemove={() => field.onChange(null)}
              />
              {errors.imageUrl && (
                <ErrorText>{errors.imageUrl.message}</ErrorText>
              )}
            </>
          )}
        />
      </FormSection>

      <FormSection>
        <SectionLabel>
          {formatMessage({
            id: 'newBox.additionalLabel',
            defaultMessage: 'Видимость бокса',
          })}
        </SectionLabel>

        <Controller
          name="publishToFeed"
          control={control}
          render={({ field }) => (
            <Switch
              checked={field.value}
              onChange={field.onChange}
              label={formatMessage({
                id: 'newBox.publishToFeed',
                defaultMessage: 'Выводить в ленту',
              })}
              description={formatMessage({
                id: 'newBox.publishToFeedDescription',
                defaultMessage: 'Дополнительная модерация',
              })}
            />
          )}
        />
      </FormSection>

      <SubmitButton
        onClick={handleCreateButtonClick}
        textColor={colors.white}
        textWeight={700}
      >
        {formatMessage({
          id: 'newBox.createButton',
          defaultMessage: 'Создать',
        })}
      </SubmitButton>

      <BottomSheet ref={simpleSheet.ref}>
        <CreateBoxConfirmDialog
          onConfirm={handleSubmit(handleNewBoxFormSubmit)}
          data={{
            commission: `${formatTon(boxCreationFee)} TON`,
            earnings: `${watchedCommission}%`,
          }}
        />
      </BottomSheet>

      <BottomSheet ref={howItWorksSheet.ref}>
        <HowItWorksContent onClose={howItWorksSheet.close} />
      </BottomSheet>
    </Container>
  )
}

const SectionLabel = (props: Partial<ComponentProps<typeof Typography>>) => (
  <Typography fontSize={12} fontWeight={400} {...props} />
)

const Container = styled.form`
  display: grid;
  gap: 16px;
  background: ${colors.bgPrimary};
  width: 100%;
  margin: 0 auto;
`

const FormSection = styled.div`
  display: grid;
  gap: 6px;
`

const AddButton = styled(ButtonRegular)`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${colors.secondary};
  height: 32px;
  padding: 8px 12px;
`

const SubmitButton = styled(ButtonRegular)`
  margin-top: 16px;
  width: 100%;
  background: ${colors.secondary};
  color: ${colors.white};
`

const DatePickerWrapper = styled.div`
  .date-picker-wrapper {
    width: 100%;
  }

  .date-picker-input {
    height: 42px;
    width: 100%;
    display: block;
    padding: 12px 16px;
    border-radius: 16px;
    border: 1px solid ${colors.borderPrimary};
    background-color: ${colors.inputBg};
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    font-weight: 400;
    outline: none;
    color: ${colors.inputText};
    box-sizing: border-box;
    transition: border-color 0.2s ease;

    &:focus {
      outline: none;
      border-color: ${colors.borderFocus};
    }

    &:hover {
      border-color: ${colors.borderHover};
    }
  }

  .react-datepicker {
    background: ${colors.bgPrimary};
    border: 1px solid ${colors.borderPrimary};
    border-radius: 12px;
    font-family: 'Roboto', sans-serif;
  }

  .react-datepicker__header {
    background: ${colors.bgSecondary};
    border-bottom: 1px solid ${colors.borderPrimary};
    border-radius: 12px 12px 0 0;
  }

  .react-datepicker__current-month,
  .react-datepicker-time__header {
    color: ${colors.textPrimary};
    font-weight: 500;
  }

  .react-datepicker__day-name,
  .react-datepicker__day,
  .react-datepicker__time-name {
    color: ${colors.textPrimary};
  }

  .react-datepicker__day {
    border-radius: 0.3rem;
  }

  .react-datepicker__day:hover {
    background: ${colors.bgTertiary};
    border-radius: 0.3rem;
  }
  .react-datepicker__day--selected {
    background: ${colors.buttonPrimaryBg};
  }
  .react-datepicker__day--selected:hover {
    background: ${colors.buttonPrimaryBg};
  }

  .react-datepicker__day--keyboard-selected {
    background: ${colors.bgTertiary};
  }

  .react-datepicker__day--disabled,
  .react-datepicker__time-list-item--disabled {
    color: ${colors.textDisabled} !important;
    cursor: not-allowed !important;
  }
`

const TimezoneInfo = styled.div`
  margin-top: 4px;
  color: ${colors.textSecondary};
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const StyledQuestionIcon = styled(QuestionIcon)`
  width: 24px;
  height: 24px;
`

const StyledPlusIcon = styled(PlusIcon)`
  width: 16px;
  height: 16px;
`

const AddOptionButtonContent = styled.span`
  display: grid;
  align-items: center;
  justify-content: center;
  grid-auto-flow: column;
  gap: 4px;
`
